<script setup lang="tsx">
import { type EditMethod, type EditModule, type ModuleMeta } from '@/models/api-generated'
import http from '@/services/http'
import { onBeforeMount, ref, watch } from 'vue'
import sliderStore from '@/stores/slider'
import MethodsView from '../MethodsView.vue'
import ModuleForm from '../ModuleForm.vue'
import MethodsForm from '../MethodsForm.vue'
import CancelSaveButtons from '@/components/widgets/CancelSaveButtons.vue'
import ModuleTabs from '../ModuleTabs.vue'
import { Tabs } from '@/types/module'
import { useModule } from '@/composables/useModule'
import Modal from '@/components/shared/Modal.vue'
import { useI18n } from 'vue-i18n'
import AssetsForm from '../AssetsForm.vue'
import { moduleRefreshCallback } from '../shared'
import { System } from '@/models/module-enums-generated'
import { useModuleFormValidation } from '@/composables/useModuleFormValidation'
import type { ModuleForm as Module, MethodForm } from '@/types/module'
import { cloneDeep } from 'lodash'

const props = defineProps<{ metaId: number; name: string; isCustom: boolean; initialTab?: Tabs }>()
const slider = sliderStore()

const { t } = useI18n()
const { moduleMeta, moduleForm, methodsForm } = useModule(props.metaId)
const { formRef, activeTab, validateForm, triggerFormSubmit } = useModuleFormValidation()
const originalModuleMeta = ref<ModuleMeta>()
const originalModule = ref<Module>()
const originalMethods = ref<MethodForm[]>()

const showDialog = ref(false)
const deletedMethods = ref<number[]>([])
const deletedRules = ref<number[]>([])
const modelActiveTab = defineModel<Tabs>('activeTab', { default: Tabs.Module })

const onDeleteMethod = (id?: number) => id && deletedMethods.value.push(id)
const onDeleteRule = (id?: number) => id && deletedRules.value.push(id)

watch(
	() => moduleMeta.value,
	(val) => {
		if (val.id && !originalModuleMeta.value) {
			originalModuleMeta.value = cloneDeep(val)
		}

		slider.setUnsavedChanges(slider.hasUnsavedChanges(originalModuleMeta.value, moduleMeta.value))
	},
	{ deep: true },
)

watch(
	() => moduleForm.value,
	(val) => {
		if (val.id && !originalModule.value) {
			originalModule.value = cloneDeep(val)
		}
		slider.setUnsavedChanges(slider.hasUnsavedChanges(originalModule.value, moduleForm.value))
	},
	{ deep: true },
)

watch(
	() => methodsForm.value,
	(val) => {
		if (val && !originalMethods.value) {
			originalMethods.value = cloneDeep(val)
		}
		slider.setUnsavedChanges(slider.hasUnsavedChanges(originalMethods.value, methodsForm.value))
	},
	{ deep: true },
)
watch(
	() => modelActiveTab.value,
	(v) => {
		if (v !== activeTab.value) activeTab.value = v
	},
	{ immediate: true },
)
watch(
	() => activeTab.value,
	(v) => {
		if (v !== modelActiveTab.value) modelActiveTab.value = v
	},
)
watch(
	() => props.initialTab,
	(tab) => {
		if (tab) modelActiveTab.value = tab
	},
	{ immediate: true },
)

async function cancel() {
	const { metaId, name, isCustom } = props
	const doCancel = async () => {
		slider.openViewModule(metaId, name, isCustom, modelActiveTab.value)
	}
	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		doCancel()
	}
}

onBeforeMount(() => {
	slider.setFooter(<CancelSaveButtons saveId="save-module" cancelId="cancel-module" onCancel={() => cancel()} onSave={triggerFormSubmit} />)
})

const onSubmit = async () => {
	if (!(await validateForm())) return

	const responsibleChanged =
		moduleMeta.value?.responsible?.id !== moduleForm.value?.responsible?.id ||
		moduleMeta.value?.responsible?.type !== moduleForm.value?.responsible?.type

	if (responsibleChanged) {
		showDialog.value = true
	} else {
		submit(false)
	}
}

const submit = async (overwriteResponsible: boolean) => {
	const payload: EditModule = {
		id: moduleForm.value.id!,
		name: moduleForm.value.name,
		description: moduleForm.value.description,
		type: moduleForm.value.type,
		isCustom: moduleForm.value.isCustom,
		meta: {
			id: moduleMeta.value.id,
			instanceId: moduleMeta.value.instance.id,
			responsible: moduleForm.value.responsible,
			active: moduleForm.value.active,
			overwriteResponsible,
			assetAssociations: moduleForm.value.assetAssociations,
			urlReferences: moduleForm.value.urlReferences,
			documentReferences: moduleForm.value.documentReferences,
		},
		methods: [],
		deletedMethods: deletedMethods.value,
		deletedRules: deletedRules.value,
		newMethods: [],
		newRules: [],
	}

	methodsForm.value.forEach((m) => {
		if (m.id) {
			const method: EditMethod = {
				id: m.id,
				name: m.name,
				description: m.description,
				type: m.type,
				moduleType: moduleForm.value.type,
				confidentiality: m.confidentiality,
				integrity: m.integrity,
				availability: m.availability,
				rules: [],
				status: m.status,
			}

			m.rules.forEach((r) => {
				if (!r.id) {
					payload.newRules.push({
						value: r.value,
						methodId: m.id,
					})
				} else {
					method.rules.push({
						id: r.id,
						value: r.value,
						methodId: m.id!,
					})
				}
			})

			payload.methods.push(method)
		} else {
			payload.newMethods.push(m)
		}
	})

	await http.post<EditModule>('api/module/edit', payload)

	moduleRefreshCallback.value?.()
	slider.setUnsavedChanges(false)
	slider.close()
}
</script>

<template>
	<form ref="formRef" @submit.prevent="onSubmit" class="needs-validation" novalidate>
		<ModuleTabs v-model:override="modelActiveTab">
			<template #module>
				<ModuleForm v-model="moduleForm" mode="edit"></ModuleForm>
				<AssetsForm v-if="moduleMeta.module.type === System" v-model="moduleMeta.assetAssociations"></AssetsForm>
				<Modal v-model="showDialog">
					<template #header>
						<h5>{{ t('misc.confirm') }}</h5>
					</template>
					<template #body>
						<p>{{ t('module.overwriteText') }}</p>
						<p>{{ t('module.replaceText') }}</p>
					</template>
					<template #footer>
						<button data-test="cancel-responsible-replace" class="btn btn-danger" data-bs-dismiss="modal">
							{{ t('misc.cancel') }}
						</button>
						<button data-test="replace-unassigned" class="btn btn-secondary me-2" @click="submit(false)" data-bs-dismiss="modal">
							{{ t('module.replaceUnassigned') }}
						</button>
						<button data-test="replace-all" class="btn btn-primary" @click="submit(true)" data-bs-dismiss="modal">
							{{ t('module.replaceAll') }}
						</button>
					</template>
				</Modal>
			</template>
			<template #measures>
				<MethodsForm
					v-if="moduleMeta.module.isCustom"
					v-model="methodsForm"
					:onDeleteMethod="onDeleteMethod"
					:onDeleteRule="onDeleteRule"
				></MethodsForm>
				<MethodsView v-else v-model="methodsForm" :is-edit="true"></MethodsView>
			</template>
		</ModuleTabs>
	</form>
</template>
